import { GridApi, IRowNode } from "@ag-grid-community/core";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { RoundingMethodologyEnum } from "gen/model/roundingMethodologyEnum";
import { produce, WritableDraft } from "immer";
import { cloneDeep, isEqual } from "lodash-es";
import { FundFamilyUseAllocationTableContext } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { fundFamilyUseAllocationTableRefreshTotals } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { FundFamilyUseAllocationTableAmountFrontend } from "shared/components/fund-family-use-allocation/types";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { sumMoneyStrings } from "shared/utils/MoneyUtils";

export const fundFamilyUseAllocationTableCalculateAmounts = (params: {
  api: GridApi<FundFamilyUseAllocationTableRow>;
  node: IRowNode<FundFamilyUseAllocationTableRow> | null;
  context: FundFamilyUseAllocationTableContext;
}) => {
  const { api, node, context } = params;
  if (!node?.data || node.rowIndex === null) {
    return;
  }

  const {
    tableDef,
    onBeforeRowChange,
    onRowChange,
    getAllocation,
    roundingMethodology,
  }: FundFamilyUseAllocationTableContext = context;

  const roundingType = roundingMethodology || RoundingMethodologyEnum.cents;
  const row = node.data;

  let newData = produce(cloneDeep(row), (draft) => {
    tableDef.amount_types.forEach((amountType) => {
      // if its management fee type, then skip it since the amount is derived from the fee instead of inputted
      const fundFamilyAmount = draft.fund_family[amountType.id] as
        | WritableDraft<FundFamilyUseAllocationTableAmountFrontend>
        | undefined;
      if (draft.is_auto_allocate) {
        if (draft.category_id === "management_fees") {
          return;
        }
        const affectedFundIds = Object.keys(row.funds);
        affectedFundIds.forEach((fundId) => {
          const fundDef = tableDef.funds.find(
            (fund) => fund.fund_id === fundId,
          )!;
          const allocation = getAllocation?.({
            context,
            row,
            fundDef,
            amountTypeDef: amountType,
          });
          if (getAllocation && !allocation) {
            // waiting for allocation to be loaded (see useGetAllocationsByLookupKey)
            return;
          }
          const allocationPercentage =
            allocation?.ownershipPercentage ??
            decimalOrZero((1 / affectedFundIds.length).toString());

          const rawAmount = fundFamilyAmount
            ? allocationPercentage.times(decimalOrZero(fundFamilyAmount.amount))
            : decimalOrZero("0");
          const roundedAmount =
            roundingType === RoundingMethodologyEnum.dollar
              ? rawAmount.round().toFixed(2)
              : rawAmount.toFixed(2);
          const amount = fundFamilyAmount ? roundedAmount : "";
          const fundElement = draft.funds[fundId][amountType.id] as
            | WritableDraft<FundFamilyUseAllocationTableAmountFrontend>
            | undefined;
          if (fundElement) {
            fundElement.amount = amount;
            fundElement.unroundedAmount = rawAmount.toString();
          } else {
            (draft.funds[fundId][
              amountType.id
            ] as FundFamilyUseAllocationTableAmountFrontend) = {
              amount,
              unroundedAmount: rawAmount.toString(),
            };
          }
        });
      } else {
        const amount = sumMoneyStrings(
          Object.values(draft.funds).map((fundAmounts) => {
            const fundAmount = fundAmounts[amountType.id] as
              | WritableDraft<FundFamilyUseAllocationTableAmountFrontend>
              | undefined;
            return fundAmount?.amount ?? "0";
          }),
        );
        if (fundFamilyAmount) {
          fundFamilyAmount.amount = amount;
        } else {
          draft.fund_family[amountType.id] = { amount };
        }
      }
    });
  });

  newData =
    onBeforeRowChange?.({
      row: newData,
    }) ?? newData;

  if (!isEqual(newData, node.data)) {
    node.updateData(cloneDeep(newData));
    onRowChange({ row: newData });
    if (params.node) {
      params.api.refreshCells({
        rowNodes: [params.node],
        force: true,
      });
      fundFamilyUseAllocationTableRefreshTotals(api);
    }
  }
};
